import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  TranscriptionDTOSchema,
  UPLOAD_MAX_ATTEMPTS,
  uploadRetryDelayMs,
} from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { isRetriableNetworkError } from "@/lib/retry";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const TranscriptionsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(TranscriptionDTOSchema),
});

const TranscribeAudioResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    text: z.string(),
    transcription: TranscriptionDTOSchema,
    minutesTranscribed: z.number().optional(),
  }),
});

export function useTranscriptions(id: string | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["transcriptions", id],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/consultations/${id}/transcription`,
      });
      return TranscriptionsResponseSchema.parse(json).data;
    },
    enabled: !!id,
  });
}

export function useSaveTranscription(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    // A transcript chunk is the doctor's only record of what was said, so this
    // save is retried on network failures instead of being dropped. The
    // caller's clientRequestId keeps a replay from appending the text twice.
    mutationFn: ({
      text,
      clientRequestId,
    }: {
      text: string;
      clientRequestId?: string;
    }) =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/transcription`,
        data: { text, clientRequestId },
      }),
    retry: (failureCount, error) =>
      failureCount < UPLOAD_MAX_ATTEMPTS && isRetriableNetworkError(error),
    retryDelay: (failureCount) => uploadRetryDelayMs(failureCount + 1),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["transcriptions", id] }),
  });
}

export function useReplaceTranscription(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      backend({
        method: "PUT",
        url: `/api/v1/consultations/${id}/transcription`,
        data: { text },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["transcriptions", id] }),
  });
}

export function useLogRecordingDuration(id: string | undefined) {
  const backend = useBackendAuthenticated();
  return useMutation({
    mutationFn: (durationSeconds: number) =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/log-recording-duration`,
        data: { durationSeconds },
      }),
  });
}

export function useTranscribeAudio(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({ file, durationSeconds }: { file: File; durationSeconds: number }) => {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("durationSeconds", String(durationSeconds));
      const json = await backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/transcribe-audio`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return TranscribeAudioResponseSchema.parse(json).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["transcriptions", id] });
      toast.success(t("consultation.audioTranscribed"));
      const minutes = data.minutesTranscribed ?? 0;
      trackEvent("transcription_completed", {
        consultation_id: id ?? "",
        minutes_transcribed: minutes,
      });
    },
    onError: () => {
      toast.error(t("consultation.audioTranscribeError"));
    },
  });
}
